import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useCategory} from '@salesforce/commerce-sdk-react'
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {flatten} from '@salesforce/retail-react-app/app/utils/utils'
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'

import Header from '../../header'
import Footer from '../../footer'
import TopBar from '../../top-bar'
import {ListMenu, ListMenuContent} from '../../list-menu'
import {SearchModalProvider} from '../../../contexts/search-modal-context'
import {useGlobalNavigation} from '../../../contexts/global-elements-context'
import {useActiveCategoriesTree} from '../../../hooks/use-active-categories'
import {
    mergeCMSCategoryReplacements,
    transformCMSMenuToCategories
} from '../../optimizely/utils/utils'

import {
    CheckoutHeader,
    CheckoutFooter,
    DrawerMenu,
    AccountModal,
    AuthModal
} from '../lazy-components'

/* ------------------------------------------------------------------ */
/*  Local helper components (only used within MainLayout)              */
/* ------------------------------------------------------------------ */

// Keep the submenu visually quiet while its category data is fetched. The loaded
// content animates in below instead of replacing a spinner with the full menu.
const PlaceholderComponent = () => null

const SkipToContentLabel = () => {
    const intl = useIntl()
    return intl.formatMessage({
        id: 'skip.to.content',
        defaultMessage: 'Skip to Content'
    })
}

const ListMenuContentWithData = withCommerceSdkReact(
    ({data: rawData, ...rest}) => {
        const {data: globalNavigationData} = useGlobalNavigation()

        // Hide categories that have no online/orderable products at every level.
        // The lookup is async (non-blocking) and reuses the cached root result, so
        // by the time the popover is opened it filters immediately.
        const {prunedNode: data} = useActiveCategoriesTree(rawData, 'categories')

        // Merge category replacements from CMS to category object
        // Start with API data and merge CMS data on top
        let categoriesWithCMSData = mergeCMSCategoryReplacements(data, globalNavigationData)

        const apiData = {
            category: data
        }
        if (categoriesWithCMSData?.ShopMenuContent) {
            // Transform the CMS data to category object models to can render the entire popover from CMS
            categoriesWithCMSData = transformCMSMenuToCategories(
                [categoriesWithCMSData?.ShopMenuContent],
                apiData
            )
        }

        // If rest.item already has CMS mega menu data (from list-menu.jsx), preserve it
        // This handles the case where the CMS data was merged at a higher level
        if (rest?.item?.hasCMSMegaMenu && rest?.item?.CMSMegaMenuData) {
            categoriesWithCMSData = {
                ...categoriesWithCMSData,
                hasCMSMegaMenu: rest.item.hasCMSMegaMenu,
                CMSMegaMenuData: rest.item.CMSMegaMenuData,
                CategoryReplacement: rest.item.CategoryReplacement
            }
        }

        // Flatten the deeply-fetched category data and merge into categories map
        // This ensures nested categories have their c_relativeUrl available
        const deepCategories = flatten(data || {}, 'categories')
        const mergedCategories = {...rest?.categories, ...deepCategories}

        // Rest contains also categories which needs to be merged as well
        Object.keys(mergedCategories).forEach((key) => {
            mergeCMSCategoryReplacements(mergedCategories[key], globalNavigationData)
        })

        return (
            <Box
                data-testid="submenu-content"
                sx={{
                    '@keyframes submenuContentReveal': {
                        from: {opacity: 0, transform: 'translateY(-8px)'},
                        to: {opacity: 1, transform: 'translateY(0)'}
                    },
                    animation: 'submenuContentReveal 250ms cubic-bezier(0.22, 1, 0.36, 1)',
                    '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none'
                    }
                }}
            >
                <ListMenuContent
                    {...rest}
                    item={categoriesWithCMSData}
                    categories={mergedCategories}
                />
            </Box>
        )
    },
    {
        hook: useCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id,
                levels: 2
            }
        }),
        placeholder: PlaceholderComponent
    }
)

/* ------------------------------------------------------------------ */
/*  MainLayout                                                         */
/* ------------------------------------------------------------------ */

const MainLayout = ({
    isCart,
    isCheckout,
    isCheckoutConfirmation,
    isOnline,
    styles,
    showCountrySelectorOnFooter,
    handleShowCountrySelectorOnFooter,
    isDrawerOpen,
    onDrawerOpen,
    onDrawerClose,
    onLogoClick,
    onCartClick,
    onAccountClick,
    onWishlistClick,
    onLoyaltyClick,
    onGiftCardClick,
    categories,
    ROOT_CATEGORY,
    authModal,
    accountModalProps,
    children
}) => (
    <Box id="app" display="flex" flexDirection="column" flex={1}>
        <HideOnMobile>
            <SkipNavLink zIndex="skipLink" data-testid="skip-content">
                <SkipToContentLabel />
            </SkipNavLink>
        </HideOnMobile>

        {isCheckout && !isCheckoutConfirmation ? (
            // TODO: add dynamic status from live chat
            <CheckoutHeader isChatOnline={true} />
        ) : (
            <>
                <TopBar showCountrySelectorOnFooter={showCountrySelectorOnFooter} />
                <Box {...styles.headerWrapper}>
                    <SearchModalProvider>
                        <Header
                            isCart={isCart}
                            onMenuClick={onDrawerOpen}
                            onLogoClick={onLogoClick}
                            onMyCartClick={onCartClick}
                            onMyAccountClick={onAccountClick}
                            onWishlistClick={onWishlistClick}
                        >
                            <HideOnDesktop>
                                <DrawerMenu
                                    isOpen={isDrawerOpen}
                                    onClose={onDrawerClose}
                                    category={categories?.[ROOT_CATEGORY]}
                                    onMyAccountClick={onAccountClick}
                                    onLoyaltyClick={onLoyaltyClick}
                                    onWishlistClick={onWishlistClick}
                                    onGiftCardClick={onGiftCardClick}
                                />
                            </HideOnDesktop>

                            <HideOnMobile>
                                <ListMenu
                                    root={categories?.[ROOT_CATEGORY]}
                                    categories={categories}
                                    itemsKey="categories"
                                    itemsCountKey="onlineSubCategoriesCount"
                                    contentComponent={ListMenuContentWithData}
                                />
                            </HideOnMobile>
                        </Header>
                    </SearchModalProvider>
                </Box>
            </>
        )}

        {!isOnline && <OfflineBanner />}

        <SkipNavContent
            style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                outline: 0
            }}
        >
            <Box
                as="main"
                id="app-main"
                role="main"
                display="flex"
                flexDirection="column"
                flex="1"
                position="relative"
            >
                <OfflineBoundary isOnline={false}>{children}</OfflineBoundary>
            </Box>
        </SkipNavContent>

        {!isCheckout ? (
            <Footer onShowCountrySelectorOnFooter={handleShowCountrySelectorOnFooter} />
        ) : (
            <CheckoutFooter />
        )}

        <AuthModal {...authModal} />
        <AccountModal {...accountModalProps} />
    </Box>
)

MainLayout.propTypes = {
    isCart: PropTypes.bool,
    isCheckout: PropTypes.bool,
    isCheckoutConfirmation: PropTypes.bool,
    isOnline: PropTypes.bool,
    styles: PropTypes.object,
    showCountrySelectorOnFooter: PropTypes.bool,
    handleShowCountrySelectorOnFooter: PropTypes.func,
    isDrawerOpen: PropTypes.bool,
    onDrawerOpen: PropTypes.func,
    onDrawerClose: PropTypes.func,
    onLogoClick: PropTypes.func,
    onCartClick: PropTypes.func,
    onAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onLoyaltyClick: PropTypes.func,
    onGiftCardClick: PropTypes.func,
    categories: PropTypes.object,
    ROOT_CATEGORY: PropTypes.string,
    authModal: PropTypes.object,
    accountModalProps: PropTypes.object,
    children: PropTypes.node
}

export default MainLayout
